import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  TextField,
  FormHelperText
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../../../components/CustomButton/Button";
import axios from "axios";

interface BusinessOption {
  id: string;
  name: string;
}

interface AddCreditsDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  businesses: BusinessOption[];
  onSuccess?: () => void;
}

const apiEndpoint = process.env.REACT_APP_API_BASE_URL;

const AddCreditsDialog: React.FC<AddCreditsDialogProps> = ({
  open,
  onClose,
  userId,
  businesses,
  onSuccess
}) => {
  const [businessId, setBusinessId] = React.useState("");
  const [credits, setCredits] = React.useState("");
  const [validationError, setValidationError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const validateBusinessId = (id: string) => {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(id)) {
      return "Please enter a valid Business ID (UUID format)";
    }
    return "";
  };

  const validateCredits = (creditsValue: string) => {
    const creditsNum = parseInt(creditsValue);
    if (isNaN(creditsNum) || creditsNum <= 0) {
      return "Please enter a valid positive number for credits";
    }
    if (creditsNum > 1000000) {
      return "Credits cannot exceed 1,000,000";
    }
    return "";
  };

  const handleConfirm = async () => {
    const trimmedBusinessId = businessId.trim();
    const trimmedCredits = credits.trim();

    if (!trimmedBusinessId) {
      setValidationError("Business ID is required");
      return;
    }

    if (!trimmedCredits) {
      setValidationError("Credits are required");
      return;
    }

    const businessIdError = validateBusinessId(trimmedBusinessId);
    if (businessIdError) {
      setValidationError(businessIdError);
      return;
    }

    const creditsError = validateCredits(trimmedCredits);
    if (creditsError) {
      setValidationError(creditsError);
      return;
    }

    setValidationError("");
    setError("");
    setLoading(true);

    try {
      await axios.post(
        `${apiEndpoint}/loyaltyprogram/support/user/credits`,
        {
          UserId: userId,
          BusinessId: trimmedBusinessId,
          Credit: parseInt(trimmedCredits)
        },
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      setBusinessId("");
      setCredits("");
      setLoading(false);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        "Failed to add credits. Please try again.";
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setBusinessId("");
    setCredits("");
    setValidationError("");
    setError("");
    setLoading(false);
    onClose();
  };

  const handleBusinessIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBusinessId(e.target.value);
    if (validationError) {
      setValidationError("");
    }
    if (error) {
      setError("");
    }
  };

  const handleCreditsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredits(e.target.value);
    if (validationError) {
      setValidationError("");
    }
    if (error) {
      setError("");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
            Add Credit
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {(error || validationError) && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
            {error || validationError}
          </Alert>
        )}
        <Typography sx={{ mb: 2 }}>
          Enter the Business ID and number of credits to add for this user.
        </Typography>

        <TextField
          fullWidth
          label="Business ID"
          value={businessId}
          onChange={handleBusinessIdChange}
          disabled={loading}
          placeholder="Enter Business ID (e.g., b02d3127-1490-4341-ac71-5c8ea27b0dd0)"
          error={!!validationError && !businessId.trim()}
          sx={{ mt: 1, mb: 2 }}
        />

        <TextField
          fullWidth
          label="Credits"
          type="number"
          value={credits}
          onChange={handleCreditsChange}
          disabled={loading}
          placeholder="Enter number of credits (e.g., 2)"
          error={!!validationError && !credits.trim()}
          sx={{ mt: 1 }}
        />

        <FormHelperText sx={{ mt: 1, color: "text.secondary" }}>
          Business ID Format: UUID (e.g., xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
        </FormHelperText>
      </DialogContent>
      <DialogActions>
        <CustomButton callBack={handleClose} disabled={loading}>
          Cancel
        </CustomButton>
        <CustomButton
          callBack={handleConfirm}
          disabled={loading || !businessId.trim() || !credits.trim()}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Add Credits"
          )}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddCreditsDialog;
